// src/App.js

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

import { DoorloopjesProvider, useDoorloopjes } from './context/DoorloopjesContext';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardLayout from './layouts/DashboardLayout';
import FloatingAssistant from './components/FloatingAssistant';
import CookieConsent from './components/CookieConsent';
import PublicPageviewTracker from './components/PublicPageviewTracker';
import ErrorPage from './pages/ErrorPage';
import NotFoundPage from './pages/NotFoundPage';

// Pagina-componenten
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import DoorloopjesOverview from './pages/DoorloopjesOverview';
import DoorloopjeDetail from './pages/DoorloopjeDetail';
import DigidatesPage from './pages/DigidatesPage';
import DigidateDetail from './pages/DigidateDetail';
import ContentDetailPage from './pages/ContentDetailPage';
import ProfilePage from './pages/ProfilePage';
import AiGesprekkenPage from './pages/AiGesprekkenPage';
import ClusterBuilderPage from './pages/ClusterBuilderPage';
import WhyHowDetailPage from './pages/WhyHowDetailPage';

// Video Analyse
import VideoAnalysePage from './pages/VideoAnalysePage';
import VideoVerificatie from './components/VideoVerificatie';
import NieuweAnalysePage from './pages/NieuweAnalysePage';
import AnalyseStatusPage from './pages/AnalyseStatusPage';
import RapportPage from './pages/RapportPage';

// Nieuw toegevoegd
import NewsPage from './pages/NewsPage';
import BackgroundsPage from './pages/BackgroundsPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import SeoPage from './pages/SeoPage';
import ExampleDoorloopjePage from './pages/ExampleDoorloopjePage';
import AlleDoorloopjesPage from './pages/AlleDoorloopjesPage';
import VoorvertoningPage from './pages/VoorvertoningPage';
import OnderwerpPage from './pages/OnderwerpPage';
import NewsItemPublicPage from './pages/NewsItemPublicPage';
import HerinneringenAfmeldenPage from './pages/HerinneringenAfmeldenPage';
import ArticlePublicPage from './pages/ArticlePublicPage';
import ContactPage from './pages/ContactPage';

const ProtectedLayout = () => (
  <ProtectedRoute>
    <DashboardLayout />
  </ProtectedRoute>
);

function AppRoutes() {
  const { connectionError, loading } = useDoorloopjes();

  if (loading) return null;
  if (connectionError) return <ErrorPage />;

  return (
    <>
      <FloatingAssistant />
      <CookieConsent />
      <PublicPageviewTracker />
      <Routes>
          {/* Publieke routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/contact" element={<ContactPage />} />

          {/* Publieke SEO-pagina's */}
          <Route path="/info/:slug" element={<SeoPage />} />

          {/* Publieke voorbeeld doorloopjes */}
          <Route path="/voorbeeld/:slug" element={<ExampleDoorloopjePage />} />

          {/* Publieke voorvertoningen van de doorloopjes en de waarom- en hoe-stukken (alleen als de backend ze geeft) */}
          <Route path="/alle-doorloopjes" element={<AlleDoorloopjesPage />} />
          <Route path="/doorloopje/:slug" element={<VoorvertoningPage soort="doorloopje" />} />
          <Route path="/waarom/:slug" element={<VoorvertoningPage soort="why" />} />
          <Route path="/hoe/:slug" element={<VoorvertoningPage soort="how" />} />

          {/* Korte campagne-URL's voor socials (Instagram-bio, story-sticker, posts) */}
          <Route
            path="/praktijkkaart"
            element={<Navigate to="/voorbeeld/example-doorloopje-de-les-beginnen" replace />}
          />
          <Route
            path="/praktijkkaart-gepaste-ondersteuning"
            element={<Navigate to="/doorloopje/gepaste-ondersteuning" replace />}
          />

          {/* Publieke onderwerp-pagina's (Waar wil jij aan werken) */}
          <Route path="/onderwerp/:slug" element={<OnderwerpPage />} />

          {/* Publieke nieuws-item detailpagina */}
          <Route path="/nieuws/:slug" element={<NewsItemPublicPage />} />

          {/* Publieke artikel detailpagina */}
          <Route path="/artikel/:slug" element={<ArticlePublicPage />} />

          {/* Afmelden voor herinneringsmails (link uit de mail, zonder login) */}
          <Route path="/herinneringen/afmelden" element={<HerinneringenAfmeldenPage />} />

          {/* Beschermde routes (vereist login) */}
          <Route element={<ProtectedLayout />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/doorloopjes" element={<DoorloopjesOverview />} />
            <Route path="/doorloopjes/:slug" element={<DoorloopjeDetail />} />

            {/* Webinars */}
            <Route path="/webinars" element={<DigidatesPage />} />
            <Route path="/webinars/:slug" element={<DigidateDetail />} />

            {/* Kennisbank gesplitst */}
            <Route path="/nieuws" element={<NewsPage />} />
            <Route path="/achtergronden" element={<BackgroundsPage />} />

            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/gesprekken" element={<AiGesprekkenPage />} />
            <Route path="/cluster-builder-test" element={<ClusterBuilderPage />} />
            <Route path="/artikelen/:slug" element={<ContentDetailPage />} />
            <Route path="/why/:slug" element={<WhyHowDetailPage />} />
            <Route path="/how/:slug" element={<WhyHowDetailPage />} />

            {/* Content detailpages */}
            <Route path="/nieuws-item/:slug" element={<ContentDetailPage />} />

            {/* Video Analyse */}
            {/* VideoVerificatie: naast de login een code per e-mail (opnames zijn gevoelig) */}
            <Route path="/video-analyse" element={<VideoVerificatie><VideoAnalysePage /></VideoVerificatie>} />
            <Route path="/video-analyse/nieuw" element={<VideoVerificatie><NieuweAnalysePage /></VideoVerificatie>} />
            <Route path="/video-analyse/:documentId/rapport" element={<VideoVerificatie><RapportPage /></VideoVerificatie>} />
            <Route path="/video-analyse/:documentId" element={<VideoVerificatie><AnalyseStatusPage /></VideoVerificatie>} />
          </Route>

          {/* 404 catch-all */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <DoorloopjesProvider>
        <AppRoutes />
      </DoorloopjesProvider>
    </BrowserRouter>
  );
}

export default App;
